import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Target, Calendar, Users, Clock } from 'lucide-react';
import { useCompetitiveEvents } from '@/hooks/useCompetitiveEvents';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useGames } from '@/hooks/useGames';
import { format, differenceInDays, differenceInHours, isPast, isBefore } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { generateSEOAltText } from '@/utils/seoAltTextGenerator';

export const CompetitiveEventBanner = () => {
  const { activeEvent, upcomingEvent } = useCompetitiveEvents();
  const { language, t } = useLanguage();
  const { data: games } = useGames();

  const { data: participations } = useQuery({
    queryKey: ['event-participations', activeEvent?.id],
    queryFn: async () => {
      if (!activeEvent?.id) return [];
      
      const { data, error } = await supabase
        .from('competitive_event_participations')
        .select(`
          *,
          servers!inner (
            id,
            name,
            logo_image_url,
            games (
              name
            )
          )
        `)
        .eq('event_id', activeEvent.id)
        .order('votes_count', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error fetching event participations:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!activeEvent?.id,
  });

  // Utiliser l'événement actif ou à venir
  const displayEvent = activeEvent || upcomingEvent;

  if (!displayEvent) return null;

  const now = new Date();

  const getDisplayDate = (isoStr: string) => {
    // Si la chaîne contient déjà une info de timezone (Z ou +/-), utiliser Date normale
    if (isoStr.includes('Z') || /[+-]\d{2}:\d{2}$/.test(isoStr)) {
      return new Date(isoStr);
    }
    
    // Sinon, traiter comme une date/heure locale (sans conversion timezone)
    const cleanStr = isoStr.replace('T', ' ').substring(0, 16);
    const [datePart, timePart] = cleanStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = (timePart || '00:00').split(':').map(Number);
    
    // Créer une date locale sans conversion de timezone
    return new Date(year, month - 1, day, hour, minute);
  };

  const startDate = getDisplayDate(displayEvent.start_date);
  const endDate = getDisplayDate(displayEvent.end_date);
  
  const isUpcoming = isBefore(now, startDate);
  const isActive = !isUpcoming && isBefore(now, endDate);
  const isEnded = isPast(endDate);
  
  // Masquer la bannière 10 minutes après la fin de l'événement
  const tenMinutesAfterEnd = new Date(endDate.getTime() + 10 * 60 * 1000);
  const shouldHideBanner = isPast(tenMinutesAfterEnd);

  if (shouldHideBanner) return null;

  const getTimeInfo = () => {
    if (isUpcoming) {
      const days = differenceInDays(startDate, now);
      const hours = differenceInHours(startDate, now) % 24;
      return { 
        status: 'upcoming',
        text: days > 0 
          ? `${t('competitive_events_starts_in')} ${days}j ${hours}h` 
          : `${t('competitive_events_starts_in')} ${hours}h`,
        color: 'bg-yellow-500'
      };
    } else if (isActive) {
      const days = differenceInDays(endDate, now);
      const hours = differenceInHours(endDate, now) % 24;
      return { 
        status: 'active',
        text: days > 0 
          ? `${t('competitive_events_active')} - ${days}j ${hours}h` 
          : `${t('competitive_events_active')} - ${hours}h`,
        color: 'bg-green-500'
      };
    } else {
      const objectiveReached = totalVotes >= displayEvent.target_votes;
      return { 
        status: 'ended',
        text: objectiveReached 
          ? t('competitive_events_ended_success') 
          : t('competitive_events_ended_failed'),
        color: objectiveReached ? 'bg-green-500' : 'bg-red-500'
      };
    }
  };

  const timeInfo = getTimeInfo();
  const totalVotes = participations?.reduce((sum, p) => sum + p.votes_count, 0) || 0;
  const progressPercent = Math.min((totalVotes / displayEvent.target_votes) * 100, 100);

  const dateLocale = language === 'fr' ? fr : language === 'es' ? es : enUS;

  // Localize name/description dynamically using selected language
  const computeEventTexts = () => {
    const ids: string[] = (displayEvent as any).game_ids || [];
    if (!ids || ids.length === 0) {
      return {
        name: t('competitive_events_event_name_all_games'),
        description: t('competitive_events_event_desc_all_games')
      };
    }
    const gameNames: string[] = ids
      .map((id) => games?.find((g: any) => g.id === id)?.name)
      .filter(Boolean) as string[];

    if (gameNames.length === 1) {
      return {
        name: t('competitive_events_event_name_single_game', { game: gameNames[0] }),
        description: t('competitive_events_event_desc_single_game', {
          game: gameNames[0],
          targetVotes: displayEvent.target_votes?.toString?.() || '',
          prestigeDays: displayEvent.prestige_days?.toString?.() || '',
        }),
      };
    }
    return {
      name: t('competitive_events_event_name_multi_games', { count: gameNames.length.toString() }),
      description: t('competitive_events_event_desc_multi_games', {
        games: gameNames.join(', '),
        targetVotes: displayEvent.target_votes?.toString?.() || '',
        prestigeDays: displayEvent.prestige_days?.toString?.() || '',
      }),
    };
  };
  const { name: localizedName, description: localizedDesc } = computeEventTexts();

  return (
    <div className="mb-8 animate-fade-in">
      <Card className={`border backdrop-blur-sm ${
        isUpcoming 
          ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30'
          : 'bg-gradient-to-r from-gaming-primary/20 to-gaming-secondary/20 border-gaming-primary/30'
      }`}>
        <div className="p-6">
          {/* En-tête de l'événement */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
            <div className="flex items-center space-x-4 mb-4 lg:mb-0">
              <div className={`p-3 rounded-lg ${
                isUpcoming
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                  : 'bg-gradient-to-r from-gaming-primary to-gaming-secondary'
              }`}>
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  {isUpcoming ? '' : '🏆'} {localizedName}
                </h2>
                {localizedDesc && (
                  <p className="text-slate-300 text-sm">{localizedDesc}</p>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Badge className={`${timeInfo.color} text-white border-0`}>
                <Clock className="w-3 h-3 mr-1" />
                {timeInfo.text}
              </Badge>
              {!isUpcoming && (
                <Badge variant="outline" className="border-gaming-primary/50 text-gaming-primary">
                  <Target className="w-3 h-3 mr-1" />
                  {totalVotes} / {displayEvent.target_votes} votes
                </Badge>
              )}
              <Badge variant="outline" className={`${
                isUpcoming ? 'border-yellow-500/50 text-yellow-400' : 'border-gaming-primary/50 text-gaming-primary'
              }`}>
                <Trophy className="w-3 h-3 mr-1" />
                {t('competitive_events_top_winners', { 
                  count: displayEvent.winner_count.toString(), 
                  days: displayEvent.prestige_days.toString() 
                })}
              </Badge>
            </div>
          </div>

          {/* Barre de progression - seulement pour événements actifs */}
          {!isUpcoming && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-300 font-medium">{t('competitive_events_objective_progress')}</span>
                <span className="text-gaming-primary font-bold">{progressPercent.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-gaming-primary to-gaming-secondary transition-all duration-500 ease-out rounded-full"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Dates de l'événement */}
          <div className="flex flex-wrap gap-4 mb-6 text-sm">
            <div className="flex items-center text-slate-300">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{t('competitive_events_start_date')}: {format(startDate, 'PPp', { locale: dateLocale })}</span>
            </div>
            <div className="flex items-center text-slate-300">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{t('competitive_events_end_date')}: {format(endDate, 'PPp', { locale: dateLocale })}</span>
            </div>
          </div>

          {/* Classement des participants - seulement pour événements actifs */}
          {!isUpcoming && participations && participations.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  {t('competitive_events_current_ranking')} ({participations.length} {t('competitive_events_participants')})
                </h3>
                {participations.some((_, index) => index < displayEvent.winner_count) && (
                  <Badge className={`text-white border-0 ${
                    isEnded && totalVotes < displayEvent.target_votes
                      ? 'bg-gradient-to-r from-red-500 to-red-600'
                      : 'bg-gradient-to-r from-gaming-primary to-gaming-secondary'
                  }`}>
                    {isEnded && totalVotes < displayEvent.target_votes ? '❌' : '🏆'} 
                    {isEnded && totalVotes < displayEvent.target_votes 
                      ? t('competitive_events_objective_not_reached')
                      : t('competitive_events_top_winners', { 
                          count: displayEvent.winner_count.toString(), 
                          days: displayEvent.prestige_days.toString() 
                        })
                    }
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {participations.slice(0, 6).map((participation, index) => (
                  <div
                    key={participation.id}
                    className={`
                      flex items-center space-x-3 p-3 rounded-lg border
                      ${index < displayEvent.winner_count 
                        ? 'bg-gradient-to-r from-gaming-primary/10 to-gaming-secondary/10 border-gaming-primary/30' 
                        : 'bg-slate-800/50 border-slate-600'
                      }
                    `}
                  >
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${index < displayEvent.winner_count 
                        ? 'bg-gradient-to-r from-gaming-primary to-gaming-secondary text-white' 
                        : 'bg-slate-600 text-slate-300'
                      }
                    `}>
                      {index + 1}
                    </div>
                    
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      {participation.servers?.logo_image_url && (
                        <img 
                          src={participation.servers.logo_image_url} 
                          alt={generateSEOAltText({
                            type: 'server-logo',
                            name: participation.servers.name,
                            context: 'événement compétitif',
                            language
                          })}
                          className="w-6 h-6 rounded object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium truncate text-sm">
                          {participation.servers?.name}
                        </p>
                        <p className="text-slate-400 text-xs">
                          {participation.servers?.games?.name}
                        </p>
                      </div>
                    </div>
                    
                     <div className="flex flex-col items-end">
                       <Badge variant="outline" className="border-gaming-primary/50 text-gaming-primary text-xs mb-1">
                         {participation.votes_count} votes
                       </Badge>
                         {index < displayEvent.winner_count && (
                           <Badge className={`text-white text-xs ${
                             isEnded && totalVotes < displayEvent.target_votes
                               ? 'bg-gradient-to-r from-red-500 to-red-600'
                               : 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                           }`}>
                             {isEnded && totalVotes < displayEvent.target_votes ? '❌' : '🏆'} 
                             {isEnded && totalVotes < displayEvent.target_votes 
                               ? t('competitive_events_no_reward')
                               : t('competitive_events_winner')
                             }
                           </Badge>
                         )}
                     </div>
                  </div>
                ))}
              </div>
              
              {participations.length > 6 && (
                <p className="text-slate-400 text-center mt-3 text-sm">
                  ... {participations.length - 6} {t('competitive_events_participants')}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};